import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Icon, Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { formatVideoDuration } from '@home/utils/videoUtils';
import { isVideoAndCoverProcessing } from '../../utils';
import { UploadStatus } from '../../enums';

import type { IVideo } from '../../types';

import './index.scss';

interface IVideoCardProps {
  showDeleteIcon?: boolean;
  data: IVideo;
  onPlay?: (data: IVideo, e: React.SyntheticEvent<HTMLElement>) => void;
  onDelete?: (data: IVideo, e: React.SyntheticEvent<HTMLElement>) => void;
  className?: string;
  style?: React.CSSProperties;
}

const VideoCard: React.FC<IVideoCardProps> = (props) => {
  const { showDeleteIcon = false, data, onPlay, onDelete, style, className } = props;

  const { formatMessage } = useIntl();

  const {
    //  1、上传中，2、上传成功，3、失败
    status,
    origin_video_name: name,
    origin_video_time: duration,
    concat_video_cover: cover,
    create_time: createTime,
    result_msg: errorMsg,
  } = data || {};

  const isLoading = status === UploadStatus.Loading;
  const isProcessing = isVideoAndCoverProcessing(status);
  const isSuccess = status === UploadStatus.Success;
  const isError = status === UploadStatus.Error;

  const renderLoadingUI = () => (
    <div className="dba-video-card__cover__center__center-inner dba-video-card__cover__loading">
      <Icon type="loading" />
      <p className="dba-video-card__cover__loading__loading-text">
        {formatMessage({ id: 'dba_motion_capture_file_uploading' })}
      </p>
    </div>
  );

  const renderProcessingUI = () => (
    <div className="dba-video-card__cover__center__center-inner dba-video-card__cover__loading">
      <Icon type="loading" />
      <p className="dba-video-card__cover__loading__loading-text">
        {formatMessage({ id: 'dba_motion_capture_file_processing' })}
      </p>
    </div>
  );

  const renderSuccessUI = () => (
    <span
      className="dba-video-card__cover__icon-play"
      onClick={(e) => {
        e?.stopPropagation();
        e?.preventDefault();
        onPlay?.(data, e);
      }}
    ></span>
  );

  const renderErrorUI = () => (
    <div className="dba-video-card__cover__center__center-inner dba-video-card__cover__error">
      <span className="dba-video-card__cover__icon-play dba-video-card__cover__icon-play--error"></span>
      <Bubble dark arrowPointAtCenter content={errorMsg}>
        <p className="dba-video-card__cover__error__error-text">{errorMsg}</p>
      </Bubble>
    </div>
  );

  const renderCenterUI = () => {
    let contextUI = null;
    if (isLoading) {
      contextUI = renderLoadingUI();
    } else if (isProcessing) {
      contextUI = renderProcessingUI();
    } else if (isSuccess) {
      contextUI = renderSuccessUI();
    } else if (isError) {
      contextUI = renderErrorUI();
    }
    return <div className="dba-video-card__cover__center">{contextUI}</div>;
  };

  return (
    <div
      style={style}
      className={classnames('dba-video-card', className, {
        'dba-video-card--loading': isLoading,
        'dba-video-card--processing': isProcessing,
        'dba-video-card--success': isSuccess,
        'dba-video-card--error': isError,
      })}
    >
      <Bubble
        dark
        arrowPointAtCenter
        content={isProcessing ? formatMessage({ id: 'dba_motion_capture_file_waitting_tips' }) : undefined}
      >
        <div className="dba-video-card__cover-container">
          {/* 视频封面 */}
          {isSuccess && cover && <img className="dba-video-card__cover" alt="" src={cover} />}
          {/* 视频上传中UI、视频上传成功UI、视频上传失败UI */}
          {renderCenterUI()}
          {/* 视频时长 */}
          {[UploadStatus.Loading, UploadStatus.Processing, UploadStatus.CoverGeneratting, UploadStatus.Success].includes(status) && duration && (
            <span className="dba-video-card__cover__duration">{formatVideoDuration(duration)}</span>
          )}
          {/* hover蒙版浮层 */}
          {isSuccess && <div className="dba-video-card__cover__filter"></div>}
          {/* 删除按钮 */}
          {showDeleteIcon && !isLoading && (
            <SvgIcon
              className="dbh-general-delete-icon dba-video-card__icon-delete"
              type="delete-bg-icon"
              onClick={(e) => {
                e?.stopPropagation();
                e?.preventDefault();
                onDelete?.(data, e);
              }}
            />
          )}
        </div>
      </Bubble>
      {/* 视频名称 */}
      <div className="dba-video-card__name">{name}</div>
      {/* 视频上传时间 */}
      <div className="dba-video-card__time">{createTime}</div>
    </div>
  );
};

export default VideoCard;
